import { TableRowMoveEvent, TableRowDisplayEvent, TableRowMode, Label, TableRow, Panel, Textbox, ChangeEvent, ClickEvent, Image, Toast, Checkbox, TableRowCreationEvent, Table, Snackbar, TableContentsChangedEvent, TableAction } from "@mcleod/components";
import { Api, ModelRow, DateUtil } from "@mcleod/core";
import { ImageName } from "@mcleod/images";
import { ManifestMovementQuickInfo } from "../ManifestMovementQuickInfo";
import { YSplit } from "../YSplit";
import { PlanningUtil } from "./PlanningUtil";
import { CommonDialogs } from "@mcleod/common";
import { AutogenLayoutPlanningLinkedMoves } from "./autogen/AutogenLayoutPlanningLinkedMoves";
import { LinkedAction } from "./LinkedMoveSlideout";

export class PlanningLinkedMoves extends AutogenLayoutPlanningLinkedMoves {
    private action: LinkedAction;
    private tableSetup: boolean = false;

    setup(action: LinkedAction) {
        this.action = action;
        if (action == LinkedAction.EDIT) {
            this.labelLinkPrompt.text = "Select rows to unlink movements from the group.";
        }
    }

    performSave(): Promise<boolean> {
        if (this.action == LinkedAction.ADD) {
            return this.linkMoves();
        }
        if (this.action == LinkedAction.EDIT) {
            return this.editMoves();
        }
    }

    linkMoves() {
        const selectedRows = this.getSelectedRows();
        if (selectedRows.length < 2) {
            CommonDialogs.showError("Must have at least 2 rows selected to link moves.");
            return Promise.resolve(false);
        }
        const movementIds = selectedRows
            .map(row => `${row.get("movement_id", row.get("id"))}`)
            .join(',');
        return Api.post("lme/dispatch/planning/link-moves", { movement_ids: movementIds });
    }

    async editMoves() {
        const { movement_ids, final_sequence_ids, unlinked_movement_ids, added_movement_ids } = this.getPostingIds();
        const saveLinkedMoves = await CommonDialogs.showYesNo("Are you sure you want to save the changes to the linked moves?");
        if (saveLinkedMoves) {
            return Api.post("lme/dispatch/planning/edit-linked-moves", {
                movement_ids,
                final_sequence_ids,
                unlinked_movement_ids,
                added_movement_ids
            });
        } else {
            return Promise.resolve(false);
        }
    }

    getPostingIds(): { movement_ids: string[], final_sequence_ids: string[], unlinked_movement_ids: string[], added_movement_ids: string[] } {
        const movementIds = [];
        const finalSequenceIds = [];
        const unlinkedIds = [];
        const addedIds = [];
        this.tableEquipmentAssignments.data.forEach(row => {
            const id = `${row.get("movement_id", row.get("id"))}`;
            if (row._appending) {
                addedIds.push(id);
                finalSequenceIds.push(id);
            } else {
                movementIds.push(id);
                if (row.getBoolean("link_action_checked", false)) {
                    unlinkedIds.push(id);
                } else {
                    finalSequenceIds.push(id);
                }
            }
        });
        return {
            movement_ids: movementIds,
            final_sequence_ids: finalSequenceIds,
            unlinked_movement_ids: unlinkedIds.length > 0 ? unlinkedIds : null,
            added_movement_ids: addedIds.length > 0 ? addedIds : null
        };
    }

    getSelectedRows(): ModelRow[] {
        return this.tableEquipmentAssignments.data.filter((row: ModelRow) => {
            return row.getBoolean("link_action_checked", false);
        });
    }

    tableEquipmentAssignmentsOnContentsChanged(event: TableContentsChangedEvent) {
        if (event.getAction() == TableAction.DELETE) {
            this.tableSetup = false;
        }
    }

    tableEquipmentAssignmentsBeforeTableRowMove(event: TableRowMoveEvent) {
        const { oldIndex, newIndex } = event;
        const nextRow: TableRow = this.tableEquipmentAssignments.rows[oldIndex - newIndex > 0 ? newIndex : newIndex + (newIndex+1 < this.tableEquipmentAssignments.rows.length ? 1 : 0)];
        if (nextRow && nextRow.data.get("move_status") != "A") {
            event.stop();
            Snackbar.showWarningSnackbar("Cannot place movement before an in progress or delivered move");
        }
    }

    /** This is an event handler for the onTableRowMove event of tableEquipmentAssignments. */
    tableEquipmentAssignmentsAfterTableRowMove(event: TableRowMoveEvent) {
        this.calculateDistBetweenStops();
    }

    async calculateDistBetweenStops() {
        let priorStop = null;
        for (const tableRow of this.tableEquipmentAssignments.rows) {
            if (tableRow.visible) {
                const moveRow = tableRow.data;
                let calcDistFromPrev = null;
                if (priorStop != null) { // skip first stop
                    const distCalcProps = {
                        first_city: moveRow.get("origin_city_id"),
                        first_state: moveRow.get("origin_state"),
                        first_zip: moveRow.get("origin_zip"),
                        prior_city: priorStop.city_id,
                        prior_state: priorStop.state,
                        prior_zip: priorStop.zip_code
                    };
                    const res = await Api.post("lme/dispatch/distance-between-stops", distCalcProps);
                    if (res.data?.[0].calc_dist_from_prev) {
                        calcDistFromPrev = res.data[0].calc_dist_from_prev;
                    }
                }
                priorStop = {
                    city_id: moveRow.get("destination_city_id"),
                    state: moveRow.get("destination_state"),
                    zip_code: moveRow.get("destination_zip"),
                };
                const distBetweenStops = tableRow.findComponentById("labelDistBetweenStops") as Label;
                distBetweenStops.visible = calcDistFromPrev != null;
                if (distBetweenStops.visible) {
                    const calcDistFromPrevRow = new ModelRow(moveRow.url, false, { calc_dist_from_prev: calcDistFromPrev });
                    distBetweenStops.boundRow = calcDistFromPrevRow;
                    distBetweenStops.displayData(calcDistFromPrevRow, null, 0);
                }
            }
        }
    }

    haveRowsMoved(): boolean {
        return this.tableEquipmentAssignments.data.some((row: ModelRow) => row.getChangedData()?.hasOwnProperty("linked_sequence"));
    }

    tableEquipmentAssignmentsOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow = event.getTableRow();
        const modelRow = tableRow.data as ModelRow;
        if (!this.tableSetup) {
            this.performTableSetup(tableRow);
        }
        this.updateYsplitImage(tableRow);
        this.updateLoadedImage(tableRow);
        this.updateManifestImage(tableRow);
        tableRow.toolsCell.toolsPanel.visible = false;
        this.updateManualToolButtons(tableRow);
        const orderId = tableRow.findComponentById("labelOrderNumber") as Label;
        if (!modelRow.isNull("manifest_id")) {
           (tableRow.findComponentById("labelOrderTitle") as Label).text = "Manifest - ";
            orderId.visible = false;
            const manifestIdLabel = tableRow.findComponentById("labelManifestId") as Label;
            manifestIdLabel.visible = true;
            manifestIdLabel.addUnderlineWhenMouseOver();
        }
        else if (!modelRow.isNull("order_id"))
            orderId.addUnderlineWhenMouseOver();

        if ("A" === modelRow.get("status") && !modelRow.isNull("origin_actual_arrival"))
            tableRow.findComponentById("labelArrivedBadge").visible = true;

        this.setupNextStopWindow(tableRow, modelRow, "origin_early", "origin_late", "textboxEarlyPickup", "origin_timezone");
        this.setupNextStopWindow(tableRow, modelRow, "destination_early", "destination_late", "textboxEarlyDelivery", "destination_timezone");
    }

    private performTableSetup(tableRow: TableRow) {
        const checkbox = tableRow.findComponentById("checkboxLink") as Checkbox;
        if (!checkbox.checked) { // only set if not already checked - initial load
            checkbox.checked = this.action == LinkedAction.ADD;
            checkbox.tooltip = this.action == LinkedAction.ADD ? "Mark to link move" : "Mark to unlink move";
        }
        const status = tableRow.data.get("move_status");
        if (status != "A") {
            checkbox.visible = false;
            const imageStatus = tableRow.findComponentById("imageStatus") as Image;
            imageStatus.setProps({
                visible: true,
                name: status == "V" ? ImageName.loadVoided : status == "P" ? ImageName.loadInProgress : ImageName.loadDelivery,
                tooltip: status == "V" ? "Voided" : status == "P" ? "In Progress" : "Delivered"
            });
        }
        if (this.action == LinkedAction.EDIT && tableRow.data._appending) {
            const labelActionHistory = tableRow.findComponentById("labelActionHistory") as Label;
            labelActionHistory.visible = true;
            checkbox.visible = false;
            tableRow.allowDelete = true;
        }
        if (tableRow.index == this.tableEquipmentAssignments.rows.length - 1) {
            this.calculateDistBetweenStops();
            this.tableSetup = true;
        }
    }

    private updateManualToolButtons(tableRow: TableRow) {
        const deleteButton = tableRow?.toolsCell?.getDeleteButton();
        if (deleteButton != null && this.action == LinkedAction.EDIT && tableRow.allowDelete && tableRow.data._appending) {
            const panelToolButtons = tableRow.findComponentById("panelToolButtons") as Panel;
            panelToolButtons?.add(deleteButton);
        }
        const dragHandler = tableRow?.toolsCell?.getDragHandlerImage();
        if (dragHandler != null && tableRow?.data.get("move_status") == "A") {
            const panelToolButtons = tableRow.findComponentById("panelToolButtons") as Panel;
            panelToolButtons?.add(dragHandler);
        }
    }

    private setupNextStopWindow(tableRow: TableRow, modelRow: ModelRow, stopEarlyField: string, stopLateField: string, textboxId: string, timezoneField: string){
            const stopEarly: Date = modelRow.getDateTime(stopEarlyField, null);
            const stopLate: Date = modelRow.getDateTime(stopLateField, null);
            const stopEarlyF: string = DateUtil.formatDateTime(stopEarly, DateUtil.getUserDateTimeFormat());
            const stopLateF: string = DateUtil.formatDateTime(stopLate, DateUtil.getUserDateTimeFormat());
            const timezone: string = modelRow.getString(timezoneField, "");
            let stopWindow: string = "";

            if (stopEarly && stopLate) {
                if (stopEarly.toDateString() === stopLate.toDateString()) {
                    const lateTime = DateUtil.formatTime(stopLate, DateUtil.getUserTimeFormat());
                    stopWindow = `${stopEarlyF} - ${lateTime} ${timezone}`;
                } else {
                    stopWindow = `${stopEarlyF} -\n${stopLateF} ${timezone}`;
                }
            } else if (stopEarly) {
                stopWindow = `${stopEarlyF} ${timezone}`;
            } else if (stopLate) {
                stopWindow = `${stopLateF} ${timezone}`;
            }

            const nextStopWindowLabel = tableRow.findComponentById(textboxId) as Textbox;
            nextStopWindowLabel.text = stopWindow;
    }

    private updateYsplitImage(tableRow: TableRow) {
        const modelRow = tableRow.data;
        const imageYsplit = tableRow.findComponentById("imageYsplit") as Image;
        YSplit.setImage(modelRow.get("origin_stop_type"), modelRow.get("destination_stop_type"), imageYsplit);
        imageYsplit.visible = imageYsplit.color != null;
    }

    private updateLoadedImage(tableRow: TableRow) {
        const loadedImage = tableRow.findComponentById("imageLoaded") as Image;
        const loaded = !tableRow.data.getBoolean("emptied", false);
        loadedImage.name = loaded  ? ImageName.circleCheckEmpty : ImageName.emptyMove;
        loadedImage.tooltip = loaded ? "Loaded" : "Empty";
        if (this.action == LinkedAction.EDIT && tableRow.data._appending) {
            loadedImage.marginLeft = 30;
        }
    }

    private updateManifestImage(tableRow: TableRow) {
        const imageManifest = tableRow.findComponentById("imageManifest") as Image;
        if (imageManifest != null) {
            const manifestId = tableRow.data?.get("manifest_id");
            const movementId = tableRow.data?.get("movement_id");
            imageManifest.visible = manifestId != null;
            ManifestMovementQuickInfo.addHoverToDiscoverToManifestIdComponent(imageManifest, movementId, manifestId);
        }
    }

    checkboxLinkAllOnChange(event: ChangeEvent) {
        if (event.userInitiatedChange) {
            this.tableEquipmentAssignments.rows.forEach((row: TableRow) => {
                const checkboxLink = row.findComponentById("checkboxLink") as Checkbox;
                if (checkboxLink.visible) {
                    row.data.set("link_action_checked", event.newValue ? "Y" : "N");
                }
            });
        }
        if (this.action == LinkedAction.EDIT) {
            if (this.panelHideLinkMoves.visible) {
                this.switchHideUnlinked.checked = false;
            }
            this.panelHideLinkMoves.visible = this.showUnlinkedMoves();
        }
    }

    checkboxLinkOnChange(event: ChangeEvent) {
        this.checkboxLinkAll.checked = this.tableEquipmentAssignments.data
            .filter(row => row.get("move_status") == "A")
            .every((row: ModelRow) => row.get("link_action_checked", "N") == "Y");
        if (this.action == LinkedAction.EDIT) {
            this.panelHideLinkMoves.visible = this.showUnlinkedMoves();
            const tableRow = TableRow.getContainingTableRow(event.target as Checkbox);
            if (event.newValue && this.switchHideUnlinked.checked) {
                tableRow.visible = false;
            }
        }
    }

    private orderNumberOnClick(event: ClickEvent) {
        PlanningUtil.navigateToOrder(event);
    }

    showUnlinkedMoves(): boolean {
        return !this.checkboxLinkAll.checked &&
                this.tableEquipmentAssignments.data.some((row: ModelRow) => row.get("link_action_checked", "N") == "Y");
    }

    switchHideUnlinkedOnChange(event: ChangeEvent) {
        this.tableEquipmentAssignments.rows.forEach((row: TableRow) => {
            if (row.data.get("link_action_checked", "N") == "Y") {
                row.visible = !this.switchHideUnlinked.checked;
            }
        });
        this.checkboxLinkAll.enabled = !this.switchHideUnlinked.checked;
        this.calculateDistBetweenStops();
    }

}
